import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { 
  Heart, 
  Users, 
  Scale, 
  Building2, 
  Mail, 
  Phone, 
  MapPin,
  Menu,
  X,
  Feather,
  Music,
  Palette,
  ChevronDown,
  Calendar,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Book", href: "#booking" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "glass-header border-b border-[#E8DED4]/50 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
        <a href="#" data-testid="logo" className="font-heading text-xl md:text-2xl font-semibold text-text-main">
          Dr. Norja Cunningham
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              data-testid={`nav-${link.name.toLowerCase()}`}
              className="font-body text-text-muted hover:text-primary transition-colors duration-200"
            >
              {link.name}
            </a>
          ))}
          <a href="#booking">
            <Button 
              data-testid="nav-cta"
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-6 py-2 transition-all duration-300"
            >
              Book Now
            </Button>
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-text-main"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden glass-header border-t border-[#E8DED4]/50">
          <nav className="flex flex-col px-6 py-4 gap-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className="font-body text-text-main py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a href="#booking" onClick={() => setIsMobileMenuOpen(false)}>
              <Button 
                data-testid="mobile-nav-cta"
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full mt-2"
              >
                Book Now
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section data-testid="hero-section" className="min-h-[80vh] flex items-center pt-16 bg-background relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
        <div className="flex items-center justify-center min-h-[60vh]">
          {/* Text Content */}
          <div className="max-w-2xl text-center space-y-6 md:space-y-8">
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-text-main opacity-0 animate-fade-in-up">
              I will support you and your personal development.
            </h1>
            
            <p className="font-body text-base md:text-lg leading-relaxed text-text-muted opacity-0 animate-fade-in-up animation-delay-100">
              Empathic, culturally aware, grounded in theory and lived experience.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in-up animation-delay-200">
              <a href="#services">
                <Button 
                  data-testid="hero-services-btn"
                  className="btn-primary bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-base transition-all duration-300"
                >
                  View Services
                </Button>
              </a>
              <a href="#booking">
                <Button 
                  data-testid="hero-booking-btn"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-white rounded-full px-8 py-6 text-base transition-all duration-300"
                >
                  Schedule Now
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-text-muted">
          <span className="text-xs tracking-wider">Scroll</span>
          <ChevronDown size={20} className="animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  const credentials = [
    "Doctorate in Marriage and Family Therapy",
    "Masters in Marriage and Family Therapy",
    "Bachelors in Psychology",
    "Licensed Marriage & Family Therapist (LMFT)",
    "A.R.T. Trained"
  ];

  const creativeRoles = [
    { icon: Feather, label: "Author" },
    { icon: Music, label: "Singer" },
    { icon: Palette, label: "Producer" }
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-surface relative">
      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="space-y-6">
          {/* Profile Image */}
          <div className="flex justify-center mb-8">
            <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden shadow-lg border-4 border-background">
              <img
                src="https://customer-assets.emergentagent.com/job_dr-norja-mft/artifacts/jm3ahij7_file_00000000c1c4720c89f39b13b2b476ae.png"
                alt="Dr. Norja Cunningham"
                className="w-full h-full object-cover"
                data-testid="profile-image"
              />
            </div>
          </div>
          
          {/* Text Content */}
          <div className="space-y-6">
            <div className="space-y-2 text-center">
              <p className="font-body text-xs tracking-[0.2em] uppercase font-semibold text-accent">
                About Me
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-text-main">
                Guardian Ad Litem, Therapist & Creative
              </h2>
            </div>

            <p className="font-body text-base leading-relaxed text-text-muted">
              I am Dr. Norja Cunningham, licensed therapist with extensive training 
              in trauma therapy and family reconciliation. My approach combines academic 
              rigor with genuine empathy, drawing from both my clinical expertise and 
              lived experience.
            </p>

            <p className="font-body text-base leading-relaxed text-text-muted">
              Beyond therapy, I express myself through creative pursuits as an author, singer, and producer.
            </p>

            {/* Credentials */}
            <div className="space-y-3 pt-4">
              <h3 className="font-heading text-lg font-medium text-text-main">Credentials</h3>
              <ul className="space-y-2">
                {credentials.map((credential, index) => (
                  <li key={index} className="flex items-center gap-3 text-text-muted">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                    <span className="font-body text-sm">{credential}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Creative Roles */}
            <div className="flex gap-6 pt-4 justify-center">
              {creativeRoles.map((role, index) => (
                <div key={index} className="flex items-center gap-2 text-text-muted">
                  <role.icon size={18} className="text-accent" />
                  <span className="font-body text-sm">{role.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      icon: Heart,
      title: "Individual Therapy",
      price: "$125",
      description: "Personal one-on-one sessions focused on trauma healing, personal development, and improving your quality of life.",
      features: ["Trauma-informed care", "Culturally aware approach", "Personalized treatment"]
    },
    {
      icon: Users,
      title: "Family Coaching",
      price: "$125",
      description: "Reconciliation-focused family sessions to rebuild connections, improve communication, and heal relationships.",
      features: ["Reconciliation support", "Communication skills", "Family dynamics"]
    },
    {
      icon: Scale,
      title: "Guardian Ad Litem",
      price: "$500",
      description: "Professional advocacy services for children in legal proceedings, ensuring their best interests are represented.",
      features: ["Child advocacy", "Court representation", "Comprehensive assessments"]
    },
    {
      icon: Building2,
      title: "Consultation",
      price: "Contract",
      description: "Expert consultation services for agencies and companies seeking guidance on relationships and longevity.",
      features: ["Agency partnerships", "Professional training", "Custom agreements"]
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <p className="font-body text-xs tracking-[0.2em] uppercase font-semibold text-accent">
            Services
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-text-main">
            How I Can Support You
          </h2>
          <p className="font-body text-base text-text-muted max-w-2xl mx-auto">
            Whether you're seeking individual healing, family reconciliation, or professional consultation, 
            I offer comprehensive services tailored to your unique needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-card-${index}`}
              className="service-card bg-surface border border-[#E8DED4] rounded-3xl p-8 md:p-10"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="p-3 bg-surface-alt rounded-2xl">
                  <service.icon size={24} className="text-primary" />
                </div>
                <div className="text-right">
                  <span className="font-heading text-2xl md:text-3xl font-semibold text-primary">
                    {service.price}
                  </span>
                  {service.price !== "Contract" && (
                    <p className="text-xs text-text-muted">per session</p>
                  )}
                </div>
              </div>

              <h3 className="font-heading text-xl md:text-2xl text-text-main mb-3">
                {service.title}
              </h3>
              
              <p className="font-body text-sm text-text-muted mb-6 leading-relaxed">
                {service.description}
              </p>

              <ul className="space-y-2">
                {service.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center gap-3 text-text-muted">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                    <span className="font-body text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="font-body text-text-muted mb-4">
            Ready to begin your journey toward healing and growth?
          </p>
          <a href="#booking">
            <Button 
              data-testid="services-cta"
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-base transition-all duration-300"
            >
              Schedule Now
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Booking Section with Headway
const Booking = () => {
  return (
    <section id="booking" data-testid="booking-section" className="py-24 md:py-32 bg-surface">
      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        {/* Section Header */}
        <div className="space-y-4 mb-8">
          <p className="font-body text-xs tracking-[0.2em] uppercase font-semibold text-accent">
            Book an Appointment
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-text-main">
            Schedule Your Session
          </h2>
        </div>

        {/* Headway Link Button */}
        <a 
          href="https://care.headway.co/providers/norja-cunningham" 
          target="_blank" 
          rel="noopener noreferrer"
          data-testid="headway-booking-link"
        >
          <Button 
            className="bg-primary hover:bg-primary-hover text-white rounded-full px-10 py-6 text-lg transition-all duration-300"
          >
            <Calendar className="mr-2" size={20} />
            Schedule Now
          </Button>
        </a>

        {/* Alternative Contact */}
        <div className="mt-8">
          <p className="font-body text-sm text-text-muted">
            Have questions? <a href="#contact" className="text-primary hover:underline">Contact me directly</a>
          </p>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! I'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again or contact me directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="font-body text-xs tracking-[0.2em] uppercase font-semibold text-accent">
                Contact Me
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-text-main">
                Let's Start Your Journey
              </h2>
              <p className="font-body text-base leading-relaxed text-text-muted">
                Taking the first step is often the hardest. I'm here to listen, 
                support, and guide you through your healing process. Reach out 
                to schedule a consultation or ask any questions.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-surface rounded-xl">
                  <Mail size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-heading text-lg text-text-main">Email</h4>
                  <a 
                    href="mailto:lifelessonswithdrc@gmail.com" 
                    data-testid="contact-email"
                    className="font-body text-text-muted hover:text-primary transition-colors"
                  >
                    lifelessonswithdrc@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-surface rounded-xl">
                  <Phone size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-heading text-lg text-text-main">Phone</h4>
                  <a 
                    href="tel:+12035901554" 
                    data-testid="contact-phone"
                    className="font-body text-text-muted hover:text-primary transition-colors"
                  >
                    (203) 590-1554
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-surface rounded-xl">
                  <MapPin size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-heading text-lg text-text-main">Location</h4>
                  <p data-testid="contact-location" className="font-body text-text-muted">
                    Virtual sessions in CT
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-surface rounded-3xl p-8 md:p-10 shadow-sm border border-[#E8DED4]/50">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-body text-sm text-text-main">
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-background border-[#E8DED4] focus:border-primary focus:ring-primary rounded-xl py-6"
                  placeholder="Your name"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body text-sm text-text-main">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-background border-[#E8DED4] focus:border-primary focus:ring-primary rounded-xl py-6"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-body text-sm text-text-main">
                    Phone (Optional)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-background border-[#E8DED4] focus:border-primary focus:ring-primary rounded-xl py-6"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="service" className="font-body text-sm text-text-main">
                  Service Interested In
                </Label>
                <Select value={formData.service} onValueChange={handleServiceChange}>
                  <SelectTrigger 
                    data-testid="contact-service-select"
                    className="bg-background border-[#E8DED4] focus:border-primary focus:ring-primary rounded-xl py-6"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual Therapy ($125)</SelectItem>
                    <SelectItem value="family">Family Coaching ($125)</SelectItem>
                    <SelectItem value="guardian">Guardian Ad Litem ($500)</SelectItem>
                    <SelectItem value="consultation">Consultation (Contract)</SelectItem>
                    <SelectItem value="other">Other / General Inquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="font-body text-sm text-text-main">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-input"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-background border-[#E8DED4] focus:border-primary focus:ring-primary rounded-xl resize-none"
                  placeholder="what is your story or request?"
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-base transition-all duration-300 relative z-10"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section with Books
const Footer = () => {
  const currentYear = new Date().getFullYear();

  const books = [
    {
      title: "#LifeLessons: Facebook Wisdom",
      url: "https://a.co/d/0hJs32H5"
    },
    {
      title: "#LifeLessons: Facebook WISDOM Revamped Workbook",
      url: "https://a.co/d/03sisQcE"
    },
    {
      title: "The Nature of Relationships: Principles for a Prosperous Relationship with Yourself, Family, Friends and Community",
      url: "https://a.co/d/069yX2c1"
    }
  ];

  return (
    <footer data-testid="footer" className="py-16 md:py-24 bg-surface-alt">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center space-y-6">
          <h3 className="font-heading text-2xl md:text-3xl text-text-main">
            Dr. Norja Cunningham
          </h3>
          <p className="font-body text-sm text-text-muted">
            LMFT | A.R.T. Trained Trauma Therapist | Family Coach | Guardian Ad Litem | Consultant
          </p>
          
          <div className="decorative-line mx-auto" />
          
          <p className="font-body text-sm text-text-muted max-w-md mx-auto">
            Empathic, culturally aware, grounded in theory and lived experience. 
            I will support you and your personal development.
          </p>

          {/* Books Section */}
          <div className="pt-8 border-t border-[#E8DED4]/50 mt-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <BookOpen size={20} className="text-accent" />
              <h4 className="font-heading text-lg text-text-main">My Books</h4>
            </div>
            <ul className="space-y-3 max-w-2xl mx-auto">
              {books.map((book, index) => (
                <li key={index}>
                  <a
                    href={book.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`book-link-${index}`}
                    className="font-body text-sm text-text-muted hover:text-primary transition-colors underline decoration-[#E8DED4] hover:decoration-primary"
                  >
                    {book.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center gap-8 pt-8">
            <a 
              href="#about" 
              className="font-body text-sm text-text-muted hover:text-primary transition-colors"
            >
              About
            </a>
            <a 
              href="#services" 
              className="font-body text-sm text-text-muted hover:text-primary transition-colors"
            >
              Services
            </a>
            <a 
              href="#booking" 
              className="font-body text-sm text-text-muted hover:text-primary transition-colors"
            >
              Book
            </a>
            <a 
              href="#contact" 
              className="font-body text-sm text-text-muted hover:text-primary transition-colors"
            >
              Contact
            </a>
          </div>

          <p className="font-body text-xs text-text-muted pt-8">
            © {currentYear} Dr. Norja Cunningham. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const Home = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <Hero />
        <About />
        <Services />
        <Booking />
        <Contact />
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

// Data Deletion Request Page
const DataDeletionRequest = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    reason: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/data-deletion-request`, formData);
      setSubmitted(true);
      toast.success("Your data deletion request has been submitted.");
    } catch (error) {
      toast.error("Something went wrong. Please contact us directly at lifelessonswithdrc@gmail.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-16 px-6">
      <div className="max-w-2xl mx-auto">
        <a href="/" className="text-primary hover:underline mb-8 inline-block">&larr; Back to Home</a>
        
        <h1 className="font-heading text-3xl md:text-4xl text-text-main mb-4">
          Data Deletion Request
        </h1>
        <p className="font-body text-text-muted mb-2">
          <strong>Dr. Norja Cunningham, LMFT</strong> — Guardian Ad Litem, Therapist & Creative
        </p>
        
        <div className="bg-surface rounded-2xl p-6 md:p-8 mt-8 border border-[#E8DED4]">
          <p className="font-body text-text-muted text-sm mb-6">
            <strong>Retention period:</strong> Data is deleted within 30 days of an approved request, except where legal retention is required.
          </p>
          
          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <h3 className="font-heading text-lg text-green-800 mb-2">Request Submitted</h3>
              <p className="font-body text-green-700 text-sm">
                Your data deletion request has been received. You will receive a confirmation email within 48 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="font-heading text-lg text-text-main mb-3">Submit Deletion Request</h3>
              
              <div className="space-y-2">
                <Label htmlFor="name" className="font-body text-sm text-text-main">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-background border-[#E8DED4] rounded-xl py-5"
                  placeholder="Your full name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body text-sm text-text-main">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-background border-[#E8DED4] rounded-xl py-5"
                  placeholder="Email used when contacting us"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reason" className="font-body text-sm text-text-main">Reason (Optional)</Label>
                <Textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  rows={3}
                  className="bg-background border-[#E8DED4] rounded-xl resize-none"
                  placeholder="Why are you requesting data deletion?"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-5"
              >
                {isSubmitting ? "Submitting..." : "Submit Deletion Request"}
              </Button>
            </form>
          )}
          
          <p className="font-body text-xs text-text-muted mt-6 text-center">
            For questions, contact us at <a href="mailto:lifelessonswithdrc@gmail.com" className="text-primary hover:underline">lifelessonswithdrc@gmail.com</a>
          </p>
        </div>
      </div>
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

// App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/data-deletion" element={<DataDeletionRequest />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
